import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { formatPrice } from "@/utils/currency";
import { ShoppingBag, Plus, Minus, X, Check, Loader2 } from "lucide-react";

export function OrderCartSummary({
  source, platform, customerName, deliveryType, tableNumber,
  cart, subtotal, discountAmount, pointsDiscount, shipping, total,
  paymentType, cardBrand, submitting,
  updateCartQty, removeFromCart, createOrder,
}) {
  return (
    <div className="w-full lg:w-[340px] lg:sticky lg:top-[73px] h-fit" data-testid="order-cart-summary">
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="p-4 border-b bg-gray-50">
          <h3 className="font-bold text-sm uppercase text-gray-700">Resumen del pedido</h3>
        </div>

        {source && (
          <div className="px-4 py-3 border-b text-sm space-y-1">
            <div className="flex justify-between gap-2">
              <span className="text-gray-500 shrink-0">Origen</span>
              <span className="font-medium capitalize truncate text-right">{source === "platform" ? platform.replace("_", " ") : source}</span>
            </div>
            {customerName && (
              <div className="flex justify-between gap-2">
                <span className="text-gray-500 shrink-0">Cliente</span>
                <span className="font-medium truncate text-right">{customerName}</span>
              </div>
            )}
            {deliveryType && (
              <div className="flex justify-between gap-2">
                <span className="text-gray-500 shrink-0">Entrega</span>
                <span className="font-medium capitalize truncate text-right">{deliveryType === "dineIn" ? `Mesa ${tableNumber}` : deliveryType}</span>
              </div>
            )}
          </div>
        )}

        <ScrollArea className="max-h-[300px]">
          <div className="p-4 space-y-3">
            {cart.length === 0 ? (
              <p className="text-center text-gray-400 text-sm py-4">Sin productos</p>
            ) : cart.map(item => (
              <div key={item.product_id} className="flex flex-col gap-1" data-testid={`summary-item-${item.product_id.slice(-6)}`}>
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-medium leading-tight flex-1">{item.product_name}</p>
                  <button className="text-red-400 hover:text-red-600 shrink-0 mt-0.5" onClick={() => removeFromCart(item.product_id)}>
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="flex items-center gap-1.5">
                  <button className="w-6 h-6 rounded border flex items-center justify-center hover:bg-gray-50" onClick={() => updateCartQty(item.product_id, -1)}>
                    <Minus className="w-3 h-3" />
                  </button>
                  <span className="text-sm font-bold w-5 text-center">{item.quantity}</span>
                  <button className="w-6 h-6 rounded border flex items-center justify-center hover:bg-gray-50" onClick={() => updateCartQty(item.product_id, 1)}>
                    <Plus className="w-3 h-3" />
                  </button>
                  <span className="text-sm font-medium ml-auto">{formatPrice(item.quantity * item.unit_price)}</span>
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>

        <div className="p-4 border-t space-y-1.5">
          <div className="flex justify-between text-sm"><span className="text-gray-500">Subtotal</span><span>{formatPrice(subtotal)}</span></div>
          {discountAmount > 0 && <div className="flex justify-between text-sm text-emerald-600"><span>Descuento</span><span>-{formatPrice(discountAmount)}</span></div>}
          {pointsDiscount > 0 && <div className="flex justify-between text-sm text-amber-600"><span>⭐ Pontos</span><span>-{formatPrice(pointsDiscount)}</span></div>}
          {shipping > 0 && <div className="flex justify-between text-sm"><span className="text-gray-500">Flete</span><span>{formatPrice(shipping)}</span></div>}
          <Separator />
          <div className="flex justify-between font-bold text-lg">
            <span>Total</span>
            <span className="text-emerald-700" data-testid="order-total-display">{formatPrice(total)}</span>
          </div>
        </div>

        <div className="p-4 border-t">
          <Button
            className={`w-full h-12 text-base font-bold gap-2 ${!paymentType ? "bg-gray-400 hover:bg-gray-500" : "bg-emerald-600 hover:bg-emerald-700"}`}
            onClick={() => {
              if (!paymentType || (paymentType === "card" && !cardBrand)) {
                document.querySelector('[data-testid="payment-fiado"]')?.closest("div.space-y-3,div.space-y-4,section")?.scrollIntoView({ behavior: "smooth" });
                document.querySelector('[data-testid="payment-fiado"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
              } else {
                createOrder();
              }
            }}
            disabled={submitting || cart.length === 0}
            data-testid="create-order-btn"
          >
            {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : !paymentType ? (
              <><Check className="w-5 h-5" />Selecionar pagamento</>
            ) : (
              <><Check className="w-5 h-5" />Crear Pedido · {formatPrice(total)}</>
            )}
          </Button>
          {paymentType === "online" && (
            <p className="text-xs text-center text-gray-400 mt-2">Al crear, se redirigirá a la página de pago</p>
          )}
        </div>
      </div>
    </div>
  );
}
